import React from "react";
import styled from "styled-components";

import { GoogleIcon as GoogleIconComponent } from "../icons/AuthIcons";
import useGoogleAuth from "./useGoogleAuth";

// Google button styling matching Figma design
const GoogleButton = styled.button`
  width: 100%;
  height: 40px;
  background: white;
  border: 1px solid #eb4335;
  border-radius: 12px;
  color: #000e2a;
  font-family: "Mona Sans", sans-serif;
  font-size: 14px;
  font-weight: 700;
  line-height: 21px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  outline: none;

  &:hover {
    background: #f8f9fa;
    border-color: #eb4335;
  }

  &:active {
    background: #f1f3f4;
    border-color: #eb4335;
  }

  &:focus {
    outline: 2px solid #1a73e8;
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: #f8f9fa;
    color: #5f6368;
  }
`;

const GoogleIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  opacity: ${props => (props.isLoading ? 0.7 : 1)};
  transition: opacity 0.2s ease;
`;

const ButtonText = styled.span`
  flex: 1;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-right: 12px; /* 12px right padding as per guidelines */
`;

/**
 * Google OAuth Button Component
 *
 * Official Google button styling with Authorization Code + PKCE flow
 *
 * Features:
 * - Matches Google's official button design exactly
 * - Uses secure Authorization Code + PKCE flow
 * - Supports login_hint for account pre-selection
 * - Includes proper accessibility attributes
 * - Loading states and animations
 *
 * @param {Object} props
 * @param {string} props.text - Button text (default: "Continue with Google")
 * @param {boolean} props.disabled - Disable button
 * @param {string} props.loginHint - Optional email hint to preselect account
 * @param {Function} props.onError - Optional error callback
 */
const GoogleAuthButton = ({
  text = "Continue with Google",
  disabled = false,
  loginHint = undefined,
  onError = null,
}) => {
  const { handleGoogleAuth, isLoading, error } = useGoogleAuth({ loginHint });

  // Call optional error callback if provided
  if (error && onError) {
    onError(error);
  }

  return (
    <GoogleButton
      type="button"
      onClick={handleGoogleAuth}
      disabled={disabled || isLoading}
      aria-label={text}
    >
      <GoogleIcon isLoading={isLoading}>
        <GoogleIconComponent size="28px" />
      </GoogleIcon>
      <ButtonText>{isLoading ? "Connecting..." : text}</ButtonText>
    </GoogleButton>
  );
};

export default GoogleAuthButton;
